import { useCallback, useState } from "react";
import type { Task } from "@common/types/task";
import type { BoardPlacement } from "@design-system/Board/Board.types";
import { getCellKey } from "@design-system/Board/boardCells";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { getTaskCardEditPatch, type TaskCardEdit } from "./taskCardEdits";
import { useTaskBoardSave } from "./useTaskBoardSave";

interface UseTaskBoardCardEditsArgs {
    tasks: readonly Task[];
    getPlacements: (task: Task) => readonly BoardPlacement[];
    /** Whether the board shows a card at a placement (expanded column and lane). */
    isPlacementShown: (placement: BoardPlacement) => boolean;
}

/** An edited task on its way to other cells, and the cells it's headed for. */
interface MovingTask {
    taskId: string;
    cellsKey: string;
}

const NO_IDS: readonly string[] = [];

const getCellsKey = (
    getPlacements: UseTaskBoardCardEditsArgs["getPlacements"],
    task: Task,
) => getPlacements(task).map(getCellKey).join("|");

/**
 * Single-card edits from a chip, a shortcut or the title. Each one writes
 * into the task's cached lists at once and then saves, like a drop; view-only
 * users get the refusal and nothing changes. An edit that moves the card to
 * another column or lane makes its new card enter (`enteringItemIds`, at
 * most that one id) in the render the task lands in, unless it lands where
 * the board doesn't show it. Drops and bulk edits don't come through here.
 * `enterTask` makes a task created on the board enter the same way. An
 * edit's `confirmation` toasts once it saves, once per task and message.
 */
export const useTaskBoardCardEdits = ({
    tasks,
    getPlacements,
    isPlacementShown,
}: UseTaskBoardCardEditsArgs) => {
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const { applyUpdate, save } = useTaskBoardSave("Failed to update task");
    const [movingTask, setMovingTask] = useState<MovingTask | null>(null);
    const [enteringItemIds, setEnteringItemIds] = useState(NO_IDS);
    const latestRef = useLatestRef({ getPlacements });

    if (movingTask !== null) {
        const task = tasks.find(({ id }) => id === movingTask.taskId);
        if (task === undefined) {
            setMovingTask(null);
        } else if (getCellsKey(getPlacements, task) === movingTask.cellsKey) {
            setMovingTask(null);
            if (getPlacements(task).some(isPlacementShown)) {
                setEnteringItemIds([task.id]);
            }
        }
    }

    const editTask = useCallback(
        ({ task, body, assignee, confirmation }: TaskCardEdit) => {
            if (refuseIfViewOnly()) {
                return;
            }
            const patch = getTaskCardEditPatch({ body, assignee });
            const getRequest = () => ({
                ...applyUpdate({ task, body, patch }),
                successMessage: confirmation,
            });
            const { getPlacements: getLatestPlacements } = latestRef.current;
            const cellsKey = getCellsKey(getLatestPlacements, {
                ...task,
                ...patch,
            });
            if (cellsKey === getCellsKey(getLatestPlacements, task)) {
                save(getRequest());
                return;
            }
            const nextMovingTask = { taskId: task.id, cellsKey };
            setMovingTask(nextMovingTask);
            setEnteringItemIds(NO_IDS);
            save(getRequest(), {
                onSettled: () =>
                    setMovingTask((current) =>
                        current === nextMovingTask ? null : current,
                    ),
            });
        },
        [refuseIfViewOnly, latestRef, applyUpdate, save],
    );

    const enterTask = useCallback(
        (taskId: string) => setEnteringItemIds([taskId]),
        [],
    );

    return { editTask, enterTask, enteringItemIds };
};
